import { MaintenanceSchedulesGetResponse } from "src/model/api/response/MaintenanceSchedulesGetResponse";

import {
  GridMaintenanceCostType,
  GridRecommendProductType,
} from "../../component/lcm/RepairSchedule";
import { OrderStatus, QuotationStatus } from "../../util/Constant";
import {
  DONE,
  INSPECTION_DETAIL_BOTH,
  INSPECTION_DETAIL_INSPECTION,
  QUOTATION,
  RECOMMEND,
  RowTypes,
  TIMINGS,
  TIMING_MAIN,
} from "./LcmConst";

/** 列グループ（予定・実施・見積）。グリッドの列名（recommend1 など）の接頭辞でもある */
const DATA_TYPES = [RECOMMEND, DONE, QUOTATION];

/** 定期点検金額グリッドの行の定義 */
type RowDefinition = {
  rowType: RowTypes;
  rowTitle: string;
  /** 値を持つ列グループ。含まれない列グループの値は undefined */
  dataTypes: string[];
  /** 予定列の計算に部品交換費用の合計を使う実施年度（未指定なら使わない） */
  recommendPriceTimings?: number[];
};

const ROW_DEFINITIONS: RowDefinition[] = [
  {
    rowType: RowTypes.InspectionCost,
    rowTitle: "定期点検基本料金",
    dataTypes: DATA_TYPES,
  },
  {
    rowType: RowTypes.TotalWorkCost,
    rowTitle: "作業費用計",
    dataTypes: DATA_TYPES,
  },
  {
    rowType: RowTypes.PartsCost,
    rowTitle: "部品交換費用計",
    dataTypes: DATA_TYPES,
    recommendPriceTimings: TIMINGS,
  },
  {
    rowType: RowTypes.InspectionDetail,
    rowTitle: "点検内訳",
    dataTypes: [RECOMMEND],
  },
  {
    rowType: RowTypes.Summary,
    rowTitle: "総計",
    dataTypes: DATA_TYPES,
    recommendPriceTimings: TIMING_MAIN,
  },
  {
    rowType: RowTypes.Expenses,
    rowTitle: "経費",
    dataTypes: [DONE, QUOTATION],
  },
  {
    rowType: RowTypes.TotalSummary,
    rowTitle: "総計(経費込)",
    dataTypes: [DONE, QUOTATION],
  },
  {
    rowType: RowTypes.QuotationOrder,
    rowTitle: "見積・注文",
    dataTypes: [DONE, QUOTATION],
  },
];

// 定期点検金額データ取得
export function getGridMaintenanceCostList(
  maintenaceScheduleDataList: MaintenanceSchedulesGetResponse[],
  recommendPriceSum: GridRecommendProductType,
  imoNo: string | undefined,
  bemacFlag: boolean
) {
  const gridMaintenanceCostList: GridMaintenanceCostType[] = [];

  if (!imoNo) {
    return gridMaintenanceCostList;
  }

  ROW_DEFINITIONS.forEach((row) => {
    // 実施年度ごとの列（recommend1..30, done1..30, quotation1..30）
    const columns: Record<string, string | number | undefined> = {};
    DATA_TYPES.forEach((dataType) => {
      TIMINGS.forEach((timing) => {
        // 部品交換費用の合計（予定列で使う行・年度のみ）
        const recommendPrice =
          dataType === RECOMMEND && row.recommendPriceTimings?.includes(timing)
            ? (recommendPriceSum[
                `recommendSubtotal${timing}` as keyof GridRecommendProductType
              ] as number | undefined)
            : undefined;
        columns[`${dataType}${timing}`] = row.dataTypes.includes(dataType)
          ? getMaintenanceCostData(
              maintenaceScheduleDataList,
              timing,
              row.rowType,
              dataType,
              bemacFlag,
              recommendPrice
            )
          : undefined;
      });
    });

    gridMaintenanceCostList.push({
      rowType: row.rowType,
      rowTitle: row.rowTitle,
      ...(columns as Partial<GridMaintenanceCostType>),
    } as GridMaintenanceCostType);
  });

  return gridMaintenanceCostList;
}

function getMaintenanceCostData(
  maintenaceScheduleDataList: MaintenanceSchedulesGetResponse[],
  timing: number,
  rowType: RowTypes,
  dataType: string,
  bemacFlg: boolean,
  recomendPrice?: number
) {
  const maintenaceScheduleData = maintenaceScheduleDataList.find((it) => {
    return Number(it.inspectionYearClassValue) === timing;
  });
  switch (rowType) {
    case RowTypes.InspectionCost:
      if (dataType === RECOMMEND) {
        return maintenaceScheduleData?.inspectionCost ?? undefined;
      } else if (
        dataType === DONE &&
        maintenaceScheduleData &&
        maintenaceScheduleData.orderStatusClassValue === OrderStatus.COMPLETED
      ) {
        return maintenaceScheduleData?.doneInspectionCost ?? undefined;
      } else if (
        dataType === QUOTATION &&
        (bemacFlg ||
          maintenaceScheduleData?.quotationStatusClassValue !==
            QuotationStatus.DRAFT)
      ) {
        return maintenaceScheduleData?.quotationInspectionCost ?? undefined;
      } else {
        return undefined;
      }

    case RowTypes.TotalWorkCost:
      // 作業費用計
      // 注文は基本予定と一致するので、予定の値を設定する
      var workCost = 0;
      if (dataType === RECOMMEND) {
        workCost =
          (maintenaceScheduleData?.planTotalWorkCost
            ? maintenaceScheduleData?.planTotalWorkCost
            : 0) +
          (maintenaceScheduleData?.planTotalWorkCostWeekOut
            ? maintenaceScheduleData?.planTotalWorkCostWeekOut
            : 0) +
          (maintenaceScheduleData?.planTotalWorkCostHol
            ? maintenaceScheduleData?.planTotalWorkCostHol
            : 0) +
          (maintenaceScheduleData?.planTotalWorkCostHolOut
            ? maintenaceScheduleData?.planTotalWorkCostHolOut
            : 0) +
          (maintenaceScheduleData?.planTotalWorkCostForeign
            ? maintenaceScheduleData?.planTotalWorkCostForeign
            : 0);
      } else if (
        dataType === DONE &&
        maintenaceScheduleData &&
        maintenaceScheduleData.orderStatusClassValue === OrderStatus.COMPLETED
      ) {
        workCost =
          (maintenaceScheduleData?.doneTotalWorkCost
            ? maintenaceScheduleData?.doneTotalWorkCost
            : 0) +
          (maintenaceScheduleData?.doneTotalWorkCostWeekOut
            ? maintenaceScheduleData?.doneTotalWorkCostWeekOut
            : 0) +
          (maintenaceScheduleData?.doneTotalWorkCostHol
            ? maintenaceScheduleData?.doneTotalWorkCostHol
            : 0) +
          (maintenaceScheduleData?.doneTotalWorkCostHolOut
            ? maintenaceScheduleData?.doneTotalWorkCostHolOut
            : 0) +
          (maintenaceScheduleData?.doneTotalWorkCostForeign
            ? maintenaceScheduleData?.doneTotalWorkCostForeign
            : 0);
      } else if (
        dataType === QUOTATION &&
        (bemacFlg ||
          maintenaceScheduleData?.quotationStatusClassValue !==
            QuotationStatus.DRAFT)
      ) {
        workCost =
          (maintenaceScheduleData?.quotationTotalWorkCost
            ? maintenaceScheduleData?.quotationTotalWorkCost
            : 0) +
          (maintenaceScheduleData?.quotationTotalWorkCostWeekOut
            ? maintenaceScheduleData?.quotationTotalWorkCostWeekOut
            : 0) +
          (maintenaceScheduleData?.quotationTotalWorkCostHol
            ? maintenaceScheduleData?.quotationTotalWorkCostHol
            : 0) +
          (maintenaceScheduleData?.quotationTotalWorkCostHolOut
            ? maintenaceScheduleData?.quotationTotalWorkCostHolOut
            : 0) +
          (maintenaceScheduleData?.quotationTotalWorkCostForeign
            ? maintenaceScheduleData?.quotationTotalWorkCostForeign
            : 0);
      }
      return workCost !== 0 ? workCost : undefined;
    case RowTypes.PartsCost:
      // 部品交換費用計
      if (dataType === RECOMMEND) {
        return recomendPrice !== 0 ? recomendPrice : undefined;
      } else if (
        dataType === DONE &&
        maintenaceScheduleData &&
        maintenaceScheduleData.orderStatusClassValue === OrderStatus.COMPLETED
      ) {
        return maintenaceScheduleData?.donePartsCost ?? undefined;
      } else if (
        dataType === QUOTATION &&
        (bemacFlg ||
          maintenaceScheduleData?.quotationStatusClassValue !==
            QuotationStatus.DRAFT)
      ) {
        return maintenaceScheduleData?.quotationPartsCost ?? undefined;
      } else {
        return undefined;
      }
    case RowTypes.InspectionDetail:
      // 点検内訳
      if (dataType === RECOMMEND) {
        return maintenaceScheduleData?.inspecitonDetailClassValue ?? undefined;
      } else {
        return undefined;
      }
    case RowTypes.Summary:
      // 総計
      if (dataType === RECOMMEND) {
        let returnVal = undefined;
        if (maintenaceScheduleData) {
          returnVal =
            maintenaceScheduleData.inspecitonDetailClassValue ===
            INSPECTION_DETAIL_BOTH
              ? (maintenaceScheduleData.inspectionCost ?? 0) +
                (maintenaceScheduleData.planTotalWorkCost ?? 0) +
                (maintenaceScheduleData.planTotalWorkCostWeekOut ?? 0) +
                (maintenaceScheduleData.planTotalWorkCostHol ?? 0) +
                (maintenaceScheduleData.planTotalWorkCostHolOut ?? 0) +
                (maintenaceScheduleData.planTotalWorkCostForeign ?? 0) +
                (recomendPrice ?? 0)
              : maintenaceScheduleData.inspecitonDetailClassValue ===
                INSPECTION_DETAIL_INSPECTION
              ? maintenaceScheduleData.inspectionCost ?? 0
              : (maintenaceScheduleData.planTotalWorkCost ?? 0) +
                (maintenaceScheduleData.planTotalWorkCostWeekOut ?? 0) +
                (maintenaceScheduleData.planTotalWorkCostHol ?? 0) +
                (maintenaceScheduleData.planTotalWorkCostHolOut ?? 0) +
                (maintenaceScheduleData.planTotalWorkCostForeign ?? 0) +
                (recomendPrice ?? 0);
        }
        return returnVal;
      } else if (
        dataType === DONE &&
        maintenaceScheduleData &&
        maintenaceScheduleData.orderStatusClassValue === OrderStatus.COMPLETED
      ) {
        let returnVal = undefined;
        if (maintenaceScheduleData && maintenaceScheduleData.orderNo) {
          returnVal =
            maintenaceScheduleData.inspecitonDetailClassValue ===
            INSPECTION_DETAIL_BOTH
              ? (maintenaceScheduleData.doneInspectionCost ?? 0) +
                (maintenaceScheduleData.doneTotalWorkCost ?? 0) +
                (maintenaceScheduleData.doneTotalWorkCostWeekOut ?? 0) +
                (maintenaceScheduleData.doneTotalWorkCostHol ?? 0) +
                (maintenaceScheduleData.doneTotalWorkCostHolOut ?? 0) +
                (maintenaceScheduleData.doneTotalWorkCostForeign ?? 0) +
                (maintenaceScheduleData.donePartsCost ?? 0)
              : maintenaceScheduleData.inspecitonDetailClassValue ===
                INSPECTION_DETAIL_INSPECTION
              ? maintenaceScheduleData.doneInspectionCost ?? 0
              : (maintenaceScheduleData.doneTotalWorkCost ?? 0) +
                (maintenaceScheduleData.doneTotalWorkCostWeekOut ?? 0) +
                (maintenaceScheduleData.doneTotalWorkCostHol ?? 0) +
                (maintenaceScheduleData.doneTotalWorkCostHolOut ?? 0) +
                (maintenaceScheduleData.doneTotalWorkCostForeign ?? 0) +
                (maintenaceScheduleData.donePartsCost ?? 0);
        }
        return returnVal;
      } else if (
        dataType === QUOTATION &&
        (bemacFlg ||
          maintenaceScheduleData?.quotationStatusClassValue !==
            QuotationStatus.DRAFT)
      ) {
        let returnVal = undefined;
        if (maintenaceScheduleData) {
          returnVal =
            maintenaceScheduleData.inspecitonDetailClassValue ===
            INSPECTION_DETAIL_BOTH
              ? (maintenaceScheduleData?.quotationInspectionCost ?? 0) +
                (maintenaceScheduleData?.quotationTotalWorkCost ?? 0) +
                (maintenaceScheduleData?.quotationTotalWorkCostWeekOut ?? 0) +
                (maintenaceScheduleData?.quotationTotalWorkCostHol ?? 0) +
                (maintenaceScheduleData?.quotationTotalWorkCostHolOut ?? 0) +
                (maintenaceScheduleData?.quotationTotalWorkCostForeign ?? 0) +
                (maintenaceScheduleData?.quotationPartsCost ?? 0)
              : maintenaceScheduleData.inspecitonDetailClassValue ===
                INSPECTION_DETAIL_INSPECTION
              ? maintenaceScheduleData?.quotationInspectionCost ?? 0
              : (maintenaceScheduleData?.quotationTotalWorkCost ?? 0) +
                (maintenaceScheduleData?.quotationTotalWorkCostWeekOut ?? 0) +
                (maintenaceScheduleData?.quotationTotalWorkCostHol ?? 0) +
                (maintenaceScheduleData?.quotationTotalWorkCostHolOut ?? 0) +
                (maintenaceScheduleData?.quotationTotalWorkCostForeign ?? 0) +
                (maintenaceScheduleData?.quotationPartsCost ?? 0);
        }
        return returnVal;
      } else {
        return undefined;
      }
    case RowTypes.Expenses:
      // 経費
      if (
        dataType === DONE &&
        maintenaceScheduleData &&
        maintenaceScheduleData.orderStatusClassValue === OrderStatus.COMPLETED
      ) {
        return maintenaceScheduleData?.doneExpenses ?? undefined;
      } else if (
        dataType === QUOTATION &&
        (bemacFlg ||
          maintenaceScheduleData?.quotationStatusClassValue !==
            QuotationStatus.DRAFT)
      ) {
        return maintenaceScheduleData?.quotationExpenses ?? undefined;
      } else {
        return undefined;
      }
    case RowTypes.TotalSummary:
      // 総計(経費込み)
      if (
        dataType === DONE &&
        maintenaceScheduleData &&
        maintenaceScheduleData.orderStatusClassValue === OrderStatus.COMPLETED
      ) {
        return maintenaceScheduleData.inspecitonDetailClassValue ===
          INSPECTION_DETAIL_BOTH
          ? (maintenaceScheduleData.doneInspectionCost ?? 0) +
              (maintenaceScheduleData.doneTotalWorkCost ?? 0) +
              (maintenaceScheduleData.doneTotalWorkCostWeekOut ?? 0) +
              (maintenaceScheduleData.doneTotalWorkCostHol ?? 0) +
              (maintenaceScheduleData.doneTotalWorkCostHolOut ?? 0) +
              (maintenaceScheduleData.doneTotalWorkCostForeign ?? 0) +
              (maintenaceScheduleData.donePartsCost ?? 0) +
              (maintenaceScheduleData.doneExpenses ?? 0)
          : maintenaceScheduleData.inspecitonDetailClassValue ===
            INSPECTION_DETAIL_INSPECTION
          ? (maintenaceScheduleData.doneInspectionCost ?? 0) +
            (maintenaceScheduleData.doneExpenses ?? 0)
          : (maintenaceScheduleData.doneTotalWorkCost ?? 0) +
            (maintenaceScheduleData.doneTotalWorkCostWeekOut ?? 0) +
            (maintenaceScheduleData.doneTotalWorkCostHol ?? 0) +
            (maintenaceScheduleData.doneTotalWorkCostHolOut ?? 0) +
            (maintenaceScheduleData.doneTotalWorkCostForeign ?? 0) +
            (maintenaceScheduleData.donePartsCost ?? 0) +
            (maintenaceScheduleData.doneExpenses ?? 0);
      } else if (
        dataType === QUOTATION &&
        maintenaceScheduleData &&
        (bemacFlg ||
          maintenaceScheduleData.quotationStatusClassValue !==
            QuotationStatus.DRAFT)
      ) {
        return maintenaceScheduleData.inspecitonDetailClassValue ===
          INSPECTION_DETAIL_BOTH
          ? (maintenaceScheduleData.quotationInspectionCost ?? 0) +
              (maintenaceScheduleData?.quotationTotalWorkCost ?? 0) +
              (maintenaceScheduleData?.quotationTotalWorkCostWeekOut ?? 0) +
              (maintenaceScheduleData?.quotationTotalWorkCostHol ?? 0) +
              (maintenaceScheduleData?.quotationTotalWorkCostHolOut ?? 0) +
              (maintenaceScheduleData?.quotationTotalWorkCostForeign ?? 0) +
              (maintenaceScheduleData.quotationPartsCost ?? 0) +
              (maintenaceScheduleData.quotationExpenses ?? 0)
          : maintenaceScheduleData.inspecitonDetailClassValue ===
            INSPECTION_DETAIL_INSPECTION
          ? (maintenaceScheduleData.quotationInspectionCost ?? 0) +
            (maintenaceScheduleData.quotationExpenses ?? 0)
          : (maintenaceScheduleData?.quotationTotalWorkCost ?? 0) +
            (maintenaceScheduleData?.quotationTotalWorkCostWeekOut ?? 0) +
            (maintenaceScheduleData?.quotationTotalWorkCostHol ?? 0) +
            (maintenaceScheduleData?.quotationTotalWorkCostHolOut ?? 0) +
            (maintenaceScheduleData?.quotationTotalWorkCostForeign ?? 0) +
            (maintenaceScheduleData.quotationPartsCost ?? 0) +
            (maintenaceScheduleData.quotationExpenses ?? 0);
      } else {
        return undefined;
      }
    case RowTypes.QuotationOrder:
      // 見積・注文
      if (dataType === DONE) {
        return maintenaceScheduleData?.orderNo ?? undefined;
      } else if (dataType === QUOTATION) {
        return (
          maintenaceScheduleDataList.find((it) => {
            return Number(it.inspectionYearClassValue) === timing;
          })?.quotationNo ?? undefined
        );
      } else {
        return undefined;
      }
  }
}
